/**
 * Coach Synthesis — Temporal Context Enricher
 *
 * Decorates observations with temporal awareness drawn from the persistent
 * Coach Profile + history. Without this, every observation card reads like
 * "Coach noticed X today" — flat, no sense of memory. With it:
 *
 *   - PERSISTENT: "Setup-X has a chase penalty (4th run)"
 *     The trader has been seeing this for a month; it's not a one-off.
 *
 *   - RETURNING: "Setup-Y override-cost — recurring"
 *     Was previously resolved (didn't fire last snapshot), back today.
 *
 *   - NEW: no decoration — implicit default, the cleanest visual.
 *
 *   - FIRST_SNAPSHOT: no decoration — there's no history to compare against.
 *
 * This is "profile-aware analyzers" without modifying any analyzer file:
 *   ✓ Analyzers stay pure and scope-local
 *   ✓ Temporal context is added as METADATA on the observation, not by
 *     mutating title/detail (so unmodified renderers keep working)
 *   ✓ Synthesis layer + UI components opt-in to display the metadata
 *
 * The added metadata lives at `obs.temporalContext`:
 *   {
 *     state: 'new' | 'persistent' | 'returning' | 'first-snapshot',
 *     runs: number          — consecutive snapshots this signature has appeared in
 *     occurrences: number   — total times across all history (matches profile.entry.occurrences)
 *     firstSeenAt: ISO      — when Coach first noticed this signature
 *     lastResolvedAt: ISO?  — set when state==='returning' (when it last vanished)
 *   }
 *
 * Pure: takes observations + a runtime snapshot, returns new array. No I/O.
 */

import { signatureOf } from '../profile/buildProfile.js';

// Temporal-state constants (exported so tests + UI can switch on them).
export const TEMPORAL_STATE = Object.freeze({
  NEW: 'new',
  PERSISTENT: 'persistent',
  RETURNING: 'returning',
  FIRST_SNAPSHOT: 'first-snapshot',
});

// Persistence threshold for the priority bump. Persisting >= this many runs
// signals a leak that's not going away — boost so the synthesis surfaces it
// over fresher (lower-priority) noise.
const PERSISTENCE_PRIORITY_BUMP_AT_RUNS = 3;
const PERSISTENCE_PRIORITY_BUMP_DELTA   = 5;

/**
 * Build a fast lookup of profile entries by signature. The current profile's
 * edges + leaks both contribute. Resolved leaks land in their own map so we
 * can detect "returning" entries (signature seen previously, then resolved,
 * now back).
 */
const indexProfile = (profile) => {
  const entries = new Map();
  const resolved = new Map();
  if (!profile) return { entries, resolved };
  for (const e of (profile.edges || [])) entries.set(e.signature, e);
  for (const l of (profile.leaks || [])) entries.set(l.signature, l);
  for (const r of (profile.resolvedLeaks || [])) resolved.set(r.signature, r);
  return { entries, resolved };
};

/**
 * Walk the compact history snapshots (newest-first) and count consecutive
 * snapshots in which this signature appeared. Stops at the first gap —
 * "ran for 3 weeks, missed one, ran again" counts as 1 (the current streak),
 * not 4 (total occurrences). The profile entry's `occurrences` field
 * tracks the all-time total separately.
 */
const consecutiveRuns = (signature, history) => {
  if (!Array.isArray(history) || history.length === 0) return 1; // current snapshot only
  let runs = 1;
  for (const snap of history) {
    const present = (snap.edgeSignatures || []).includes(signature) ||
                    (snap.leakSignatures || []).includes(signature);
    if (present) runs++;
    else break;
  }
  return runs;
};

/**
 * Was this signature in some history snapshot but absent from the most
 * recent one? Returns the most recent snapshot's timestamp where it last
 * appeared — useful for the "returning" message ("last seen Apr 18").
 */
const lastSeenInHistory = (signature, history) => {
  if (!Array.isArray(history) || history.length === 0) return null;
  for (const snap of history) {
    const present = (snap.edgeSignatures || []).includes(signature) ||
                    (snap.leakSignatures || []).includes(signature);
    if (present) return snap.generatedAt || null;
  }
  return null;
};

/**
 * Compute temporal state for one observation given the indexed profile and
 * history. Returns a temporalContext object ready to attach to the
 * observation.
 */
export const computeTemporalState = (obs, { profile, history }) => {
  const signature = signatureOf(obs);
  const { entries, resolved } = indexProfile(profile);
  const inProfile = entries.has(signature);
  const wasResolved = resolved.has(signature);
  const hadHistory = Array.isArray(history) && history.length > 0;

  // Most-recent prior snapshot (history is newest-first by storeProfile).
  const priorSnapshot = hadHistory ? history[0] : null;
  const inPriorSnapshot = priorSnapshot
    ? ((priorSnapshot.edgeSignatures || []).includes(signature) ||
       (priorSnapshot.leakSignatures || []).includes(signature))
    : false;

  // No history at all → can't classify temporally; mark as first-snapshot
  // so the UI knows to omit any "(week N)" suffix.
  if (!hadHistory && !profile) {
    return {
      state: TEMPORAL_STATE.FIRST_SNAPSHOT,
      runs: 1,
      occurrences: 1,
      firstSeenAt: null,
      lastResolvedAt: null,
    };
  }

  const profileEntry = entries.get(signature);
  const occurrences = profileEntry?.occurrences || 1;
  const firstSeenAt = profileEntry?.firstSeenAt || null;
  const runs = consecutiveRuns(signature, history);

  // RETURNING: this fired before, was resolved (or absent from prior snapshot
  // entirely), and is now back. Resolved-and-back is the cleanest signal;
  // absent-and-back also counts (analyzer might have skipped a run for
  // sample-size reasons, then reactivated when data caught up).
  if (wasResolved || (!inPriorSnapshot && lastSeenInHistory(signature, history))) {
    return {
      state: TEMPORAL_STATE.RETURNING,
      runs: 1,                        // streak resets at returning
      occurrences,
      firstSeenAt,
      lastResolvedAt: resolved.get(signature)?.resolvedAt || lastSeenInHistory(signature, history),
    };
  }

  // PERSISTENT: present in current and the prior snapshot — a streak.
  if (inProfile && inPriorSnapshot) {
    return {
      state: TEMPORAL_STATE.PERSISTENT,
      runs,
      occurrences,
      firstSeenAt,
      lastResolvedAt: null,
    };
  }

  // NEW: not in prior, not in resolved — fresh signal this run.
  return {
    state: TEMPORAL_STATE.NEW,
    runs: 1,
    occurrences: 1,
    firstSeenAt,
    lastResolvedAt: null,
  };
};

/**
 * Public API: take a list of observations + a runtime snapshot, return a
 * new list with each observation annotated with temporalContext metadata.
 *
 * Synthesis observations (analyzer === 'synthesis') are passed through
 * unchanged because they're built FROM the underlying observations and
 * don't have a meaningful temporal identity of their own.
 *
 * Priority adjustment: persistent leaks/criticals at >=3 runs get a
 * +5 priority bump so the dashboard + synthesis surface them above
 * fresher noise.
 */
export const enrichWithTemporalContext = (observations, runtime = {}) => {
  if (!Array.isArray(observations) || observations.length === 0) return observations;
  const { profile, history } = runtime;
  // No profile + no history = nothing meaningful to enrich. Return as-is so
  // first-time users (or scopes called before the profile builds) get
  // unmodified observations.
  if (!profile && (!history || history.length === 0)) return observations;

  return observations.map((obs) => {
    if (!obs || obs.analyzer === 'synthesis') return obs;
    const temporalContext = computeTemporalState(obs, { profile, history });
    let priority = obs.priority;
    if (
      temporalContext.state === TEMPORAL_STATE.PERSISTENT &&
      temporalContext.runs >= PERSISTENCE_PRIORITY_BUMP_AT_RUNS &&
      (obs.severity === 'warning' || obs.severity === 'critical')
    ) {
      priority = Math.min(100, (priority || 50) + PERSISTENCE_PRIORITY_BUMP_DELTA);
    }
    return { ...obs, temporalContext, priority };
  });
};

/**
 * Helper for renderers: produce a short human-readable badge string from a
 * temporalContext object. Returns null when no badge is appropriate (NEW,
 * FIRST_SNAPSHOT, or persistent for fewer than 2 runs).
 *
 * Examples:
 *   { state: 'persistent', runs: 4 } → "4th run"
 *   { state: 'returning' }           → "returning"
 *   { state: 'persistent', runs: 2 } → "2nd run"
 *   { state: 'new' }                 → null
 */
export const temporalBadge = (temporalContext) => {
  if (!temporalContext) return null;
  if (temporalContext.state === TEMPORAL_STATE.RETURNING) return 'returning';
  if (temporalContext.state === TEMPORAL_STATE.PERSISTENT && temporalContext.runs >= 2) {
    const n = temporalContext.runs;
    const ord = n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;
    return `${ord} run`;
  }
  return null;
};

/**
 * Helper for synthesis: produce a short narrative clause about temporal
 * state, suitable for appending to the synthesis paragraph. Returns null
 * when there's nothing temporal worth saying.
 */
export const temporalNarrativeClause = (observations) => {
  if (!Array.isArray(observations) || observations.length === 0) return null;
  let returningCount = 0;
  let persistentCount = 0;
  let maxRuns = 0;
  for (const o of observations) {
    const tc = o?.temporalContext;
    if (!tc) continue;
    if (tc.state === TEMPORAL_STATE.RETURNING) returningCount++;
    if (tc.state === TEMPORAL_STATE.PERSISTENT && tc.runs >= 2) {
      persistentCount++;
      if (tc.runs > maxRuns) maxRuns = tc.runs;
    }
  }
  const parts = [];
  if (persistentCount > 0) {
    parts.push(
      maxRuns >= 4
        ? `${persistentCount} of these have been firing for ${maxRuns}+ runs — Coach has been watching`
        : `${persistentCount} of these are persisting from prior snapshots`
    );
  }
  if (returningCount > 0) {
    parts.push(`${returningCount} previously-resolved pattern${returningCount === 1 ? '' : 's'} returned`);
  }
  return parts.length > 0 ? `${parts.join('; ')}.` : null;
};

// Exports for tests
export {
  indexProfile,
  consecutiveRuns,
  lastSeenInHistory,
  PERSISTENCE_PRIORITY_BUMP_AT_RUNS,
  PERSISTENCE_PRIORITY_BUMP_DELTA,
};
